import { createAction, createSlice } from '@reduxjs/toolkit';
import { DataState } from '@shared/enums/DataState';
import { ImageUrls } from '@shared/enums/ImageUrls';
import {
  setClientContactData,
  setClientSubProjectNetworkError,
  setClientSubProjectFilters,
  setClientWorkstreamContactsData,
  setClientWorkstreamsLoading,
  setClientWorkstreamsData,
} from './actions';
import { State } from './types';
import { FeedbackGiver } from '../enums/ContactFilterOptions';
import { ClientContactData } from '@modules/EvaluationModule/interfaces/ClientSubProjectData';
import {
  ModifyClientContactData,
  AddClietContactData,
} from '@modules/EvaluationModule/interfaces/ClietContactFormData';
import { applyContactToCaches, contactCacheKeysFor } from './contactCache';

export const setClientWorkstreamDataLoading = createAction<{ workstreamId: string | null; clientId: string | null }>(
  'clientContactPage/setClientWorkstreamDataLoading'
);

const initialState: State = {
  clientContactData: { data: null, state: DataState.Pending },
  clientWorkstreamsData: {},
  clientSubProjectNetworkError: null,
  editClientDataState: DataState.Idle,
  uniqueFilterOptions: {
    status: [],
    typeOfClient: [],
    clientTitle: [],
    includeClosedWorkstreams: false,
    showFormerContacts: false,
    contactOwner: [],
    contactLocation: [],
    locationArea: [],
    feedbackGiver: Object.values(FeedbackGiver),
  },
  clientFilters: {
    status: [],
    typeOfClient: [],
    clientTitle: [],
    includeClosedWorkstreams: false,
    showFormerContacts: false,
    contactOwner: [],
    contactLocation: [],
    locationArea: [],
    feedbackGiver: [],
  },
  workstreamContactsData: {},
};

/**
 * What the add/edit sagas actually dispatch: the form payload plus the fields only the API
 * response can supply (id, dashboard registration). `contactOwner` is intentionally not part of
 * this — it's populated from Zoho/dashboard data on a real fetch, never from this form.
 */
type StoredContactPayload = Omit<ModifyClientContactData | AddClietContactData, 'id'> & {
  id: string;
  isRegisteredInClientDashboard: boolean;
  clientDashboardUserId: string;
};

function toContactData(payload: StoredContactPayload): ClientContactData {
  const title = payload.title !== 'Other' ? payload.title : payload.customTitle;
  return { ...payload, title, imageUrl: ImageUrls.Client, contactOwner: null };
}

export const slice = createSlice({
  name: 'clientContactPage',
  initialState,
  reducers: {
    setClientContactDataLoading(state) {
      state.clientContactData = { data: null, state: DataState.Pending };
    },
    setEditClientDataState(state, { payload }) {
      state.editClientDataState = payload;
    },
    addClientContactStore(state, { payload }: { payload: StoredContactPayload }) {
      const newContact = toContactData(payload);
      const isSame = (c: ClientContactData) =>
        c.id === payload.id ||
        (!!payload.emailAeTool && !!c.emailAeTool && c.emailAeTool.toLowerCase() === payload.emailAeTool.toLowerCase());
      applyContactToCaches(state.workstreamContactsData, newContact, contactCacheKeysFor(payload), isSame);
    },
    editClientContactStore(state, { payload }: { payload: StoredContactPayload }) {
      const updated = toContactData(payload);
      applyContactToCaches(
        state.workstreamContactsData,
        updated,
        contactCacheKeysFor(payload),
        (c) => c.id === payload.id
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setClientContactData, (state, { payload }) => {
      state.clientContactData = payload;
    });

    builder.addCase(setClientSubProjectFilters, (state, { payload }) => {
      state.clientFilters = { ...state.clientFilters, ...payload };
    });

    builder.addCase(setClientSubProjectNetworkError, (state, { payload }) => {
      state.clientSubProjectNetworkError = payload;
    });

    builder.addCase(setClientWorkstreamsLoading, (state, { payload }) => {
      // Stale-while-revalidate: a TTL-expired refetch shouldn't wipe what's already on screen —
      // only the fetch outcome (setClientWorkstreamsData) replaces data.
      const existing = state.clientWorkstreamsData[payload.clientId];
      state.clientWorkstreamsData[payload.clientId] = {
        data: existing?.data ?? null,
        state: DataState.Pending,
        fetchedAt: null,
      };
    });

    builder.addCase(setClientWorkstreamsData, (state, { payload }) => {
      state.clientWorkstreamsData[payload.clientId] = {
        data: payload.data,
        state: payload.state,
        fetchedAt: payload.state === DataState.Fulfilled ? Date.now() : null,
      };
    });

    builder.addCase(setClientWorkstreamDataLoading, (state, { payload }) => {
      // Same stale-while-revalidate rule as the per-client cache above.
      const key = payload.workstreamId ?? 'nullWS';
      const existing = state.workstreamContactsData[key];
      state.workstreamContactsData[key] = {
        data: existing?.data ?? null,
        state: DataState.Pending,
        fetchedAt: null,
      };
    });

    builder.addCase(setClientWorkstreamContactsData, (state, { payload }) => {
      state.workstreamContactsData[payload.workstreamId ?? 'nullWS'] = {
        data: payload.data,
        state: payload.state,
        fetchedAt: payload.state === DataState.Fulfilled ? Date.now() : null,
      };
    });
  },
});

export const { setClientContactDataLoading, editClientContactStore, addClientContactStore, setEditClientDataState } =
  slice.actions;

export default slice.reducer;
